import { searchOrders } from '@wix/ambassador-ecom-v1-order/http';
import type { Image } from '@wix/ambassador-ecom-v1-order/types';
import { addLineItemsToCurrentCart } from '@wix/ambassador-ecom-v2-cart/http';
import { queryProducts } from '@wix/ambassador-stores-catalog-v1-product/http';
import { WIX_NEW_STORES, WIX_STORES } from '@wix/app-definition-ids';
import type { IHttpClient } from '@wix/yoshi-flow-editor';

const WIX_IMAGE_PREFIX = 'wix:image://v1/';

export interface OrderItem {
  catalogItemId: string;
  productName: string;
  formattedPrice: string;
  imageUrl?: string;
  productPagePath?: string;
  productPageHref?: string;
  catalogOptions?: Record<string, any>;
  inStock?: boolean;
}

const buildStaticMediaUrl = (mediaId: string): string =>
  `https://static.wixstatic.com/media/${mediaId}`;

const toImageUrl = (image: Image | undefined): string | undefined => {
  if (!image) {
    return undefined;
  }
  if (image.url) {
    return image.url;
  }
  if (image.id) {
    if (image.id.startsWith(WIX_IMAGE_PREFIX)) {
      const mediaId = image.id.slice(WIX_IMAGE_PREFIX.length).split('/')[0];
      return buildStaticMediaUrl(mediaId);
    }
    return buildStaticMediaUrl(image.id);
  }
  return undefined;
};

export async function fetchOrderItems(
  httpClient: IHttpClient,
): Promise<OrderItem[]> {
  const { data } = await httpClient.request(
    searchOrders({
      search: {
        filter: { paymentStatus: 'PAID' },
        cursorPaging: { limit: 1 },
      },
    }),
  );

  const items: OrderItem[] = [];
  const seen = new Set<string>();

  for (const lineItem of data.orders?.[0]?.lineItems ?? []) {
    const catalogItemId = lineItem.catalogReference?.catalogItemId;
    const appId = lineItem.catalogReference?.appId;
    if (
      !catalogItemId ||
      (appId !== WIX_STORES && appId !== WIX_NEW_STORES) ||
      seen.has(catalogItemId)
    ) {
      continue;
    }
    seen.add(catalogItemId);
    const options = lineItem.catalogReference?.options as
      | Record<string, any>
      | undefined;
    items.push({
      catalogItemId,
      productName:
        lineItem.productName?.translated ??
        lineItem.productName?.original ??
        '',
      formattedPrice: lineItem.price?.formattedAmount ?? '',
      imageUrl: toImageUrl(lineItem.image),
      ...(options &&
        Object.keys(options).length > 0 && { catalogOptions: options }),
    });
  }

  return items;
}

const isProductInStock = (product: {
  stock?: { inventoryStatus?: string } | null;
}): boolean => product.stock?.inventoryStatus !== 'OUT_OF_STOCK';

/**
 * A product that manages variants has no purchasable identity on its own - the
 * buyer picks a choice on the product page. Ecom cannot resolve a bare
 * `{ catalogItemId, appId }` reference for one and answers
 * ITEM_NOT_FOUND_IN_CATALOG, so such an item is only addable when we already
 * hold the variant the member chose (order line items carry it in `options`).
 */
const canAddToCartDirectly = (
  item: OrderItem,
  manageVariants: boolean,
): boolean => !manageVariants || Boolean(item.catalogOptions);

interface CatalogVariant {
  id?: string;
  variant?: { visible?: boolean | null } | null;
  stock?: { trackQuantity?: boolean; inStock?: boolean } | null;
}

interface CatalogChoice {
  value?: string;
  inStock?: boolean;
  visible?: boolean;
}

/** `inStock` only carries meaning while the variant tracks quantity. */
const isVariantPurchasable = (variant: CatalogVariant): boolean =>
  variant.variant?.visible !== false &&
  (!variant.stock?.trackQuantity || variant.stock?.inStock !== false);

const isChoicePurchasable = (choice: CatalogChoice): boolean =>
  choice.visible !== false && choice.inStock !== false;

type ResolvedCatalogOptions =
  | { addable: true; catalogOptions?: Record<string, any> }
  | { addable: false };

/**
 * Builds the `catalogReference.options` Wix Stores documents for ecom:
 * a managed-variant product is referenced by `variantId`, an unmanaged one by
 * its option/choice pairs. Returns `addable: false` when no purchasable
 * combination exists, so the caller can leave the product out entirely.
 *
 * @see https://dev.wix.com/docs/api-reference/business-solutions/stores/catalog-v1/catalog/e-commerce-integration
 */
const resolveCatalogOptions = (product: {
  manageVariants?: boolean | null;
  variants?: CatalogVariant[] | null;
  productOptions?: { name?: string; choices?: CatalogChoice[] }[] | null;
}): ResolvedCatalogOptions => {
  if (product.manageVariants) {
    const variant = (product.variants ?? []).find(isVariantPurchasable);
    return variant?.id
      ? { addable: true, catalogOptions: { variantId: variant.id } }
      : { addable: false };
  }

  const productOptions = product.productOptions ?? [];
  if (productOptions.length === 0) {
    return { addable: true };
  }

  const choices: Record<string, string> = {};
  for (const { name, choices: optionChoices } of productOptions) {
    const choice = (optionChoices ?? []).find(isChoicePurchasable);
    if (!name || !choice?.value) {
      return { addable: false };
    }
    choices[name] = choice.value;
  }
  return { addable: true, catalogOptions: { options: choices } };
};

const toProductPagePath = (pageUrl?: {
  base?: string | null;
  path?: string | null;
}): string | undefined => pageUrl?.path ?? undefined;

const toAbsoluteProductPageUrl = (pageUrl?: {
  base?: string | null;
  path?: string | null;
}): string | undefined => {
  if (!pageUrl?.path) {
    return undefined;
  }
  const base = (pageUrl.base ?? '').replace(/\/$/, '');
  const path = pageUrl.path.startsWith('/') ? pageUrl.path : `/${pageUrl.path}`;
  return `${base}${path}`;
};

export async function enrichWithStockStatus(
  httpClient: IHttpClient,
  items: OrderItem[],
): Promise<OrderItem[]> {
  if (items.length === 0) {
    return items;
  }
  try {
    const ids = items.map((item) => item.catalogItemId);
    const { data } = await httpClient.request(
      queryProducts({
        query: {
          filter: JSON.stringify({ id: { $hasSome: ids } }),
          paging: { limit: ids.length },
        },
      }),
    );
    const productMap = new Map<
      string,
      {
        inStock: boolean;
        manageVariants: boolean;
        productPagePath?: string;
        productPageHref?: string;
      }
    >();
    for (const product of data.products ?? []) {
      if (product.id) {
        productMap.set(product.id, {
          inStock: isProductInStock(product),
          manageVariants: Boolean(product.manageVariants),
          productPagePath: toProductPagePath(product.productPageUrl),
          productPageHref: toAbsoluteProductPageUrl(product.productPageUrl),
        });
      }
    }
    return items.map((item) => {
      const match = productMap.get(item.catalogItemId);
      const isAvailable =
        (match?.inStock ?? false) &&
        canAddToCartDirectly(item, match?.manageVariants ?? false);
      return {
        ...item,
        inStock: isAvailable,
        ...(match?.productPagePath && {
          productPagePath: match.productPagePath,
        }),
        ...(match?.productPageHref && {
          productPageHref: match.productPageHref,
        }),
      };
    });
  } catch {
    // Fail closed: an unverified item may no longer exist in the catalog, and
    // offering it would make add-to-cart fail with ITEM_NOT_FOUND_IN_CATALOG.
    return items.map((item) => ({ ...item, inStock: false }));
  }
}

export async function fetchInStockOrderItems(
  httpClient: IHttpClient,
  max: number,
): Promise<OrderItem[]> {
  const items = await fetchOrderItems(httpClient);
  const enriched = await enrichWithStockStatus(httpClient, items);
  return enriched.filter((item) => item.inStock !== false).slice(0, max);
}

// Best-effort: scans a single catalog page for in-stock products.
const STORE_PRODUCTS_PAGE_SIZE = 20;

export async function fetchInStockStoreProductsAsOrderItems(
  httpClient: IHttpClient,
  limit: number,
): Promise<OrderItem[]> {
  const { data } = await httpClient.request(
    queryProducts({
      query: {
        filter: JSON.stringify({ visible: true }),
        paging: { limit: STORE_PRODUCTS_PAGE_SIZE },
      },
      // No order backs these items, so the variant to buy has to come from the
      // catalog itself - without one, ecom cannot resolve the reference.
      includeVariants: true,
    }),
  );

  const items: OrderItem[] = [];
  for (const product of data.products ?? []) {
    if (!product.id || !isProductInStock(product)) {
      continue;
    }
    const resolved = resolveCatalogOptions(product);
    if (!resolved.addable) {
      continue;
    }
    items.push({
      catalogItemId: product.id,
      productName: product.name ?? '',
      formattedPrice: product.priceData?.formatted?.price ?? '',
      imageUrl: product.media?.mainMedia?.image?.url ?? undefined,
      productPagePath: toProductPagePath(product.productPageUrl),
      ...(resolved.catalogOptions && {
        catalogOptions: resolved.catalogOptions,
      }),
    });
    if (items.length >= limit) {
      break;
    }
  }

  return items;
}

// Throws if the catalog fetch fails — the caller decides how to degrade.
export async function buildEditorBuyAgainItems(
  httpClient: IHttpClient,
  placeholders: OrderItem[],
  max: number,
): Promise<OrderItem[]> {
  const realItems = await fetchInStockStoreProductsAsOrderItems(
    httpClient,
    max,
  );
  return [...realItems, ...placeholders].slice(0, max);
}

interface AddToCartParams {
  httpClient: IHttpClient;
  catalogItemId: string;
  catalogOptions?: Record<string, any>;
}

export async function addToCart({
  httpClient,
  catalogItemId,
  catalogOptions,
}: AddToCartParams): Promise<void> {
  await httpClient.request(
    addLineItemsToCurrentCart({
      catalogItems: [
        {
          catalogReference: {
            catalogItemId,
            appId: WIX_NEW_STORES,
            ...(catalogOptions && { options: catalogOptions }),
          },
          quantity: 1,
        },
      ],
    }),
  );
}
